/**
 * ToggleGroup — segmented-control wrapper around Radix ToggleGroup.
 *
 * Used for the list/grid view toggle and any "exactly one of N" choice
 * that's denser than radio buttons. Single-mode (`type="single"`) is the
 * common shape; multi-mode is supported via Radix's prop pass-through.
 */
import * as React from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { cn } from "../lib/cn.js";

export const ToggleGroupRoot = React.forwardRef<
  React.ElementRef<typeof ToggleGroup.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroup.Root>
>(function ToggleGroupRoot({ className, ...props }, ref) {
  return (
    <ToggleGroup.Root
      ref={ref}
      className={cn(
        // Outer track: bg-bg with a hairline border (matches prototype `.view-toggle`).
        "inline-flex items-center p-0.5 rounded-sm border border-line bg-bg",
        className,
      )}
      {...(props as React.ComponentPropsWithoutRef<typeof ToggleGroup.Root>)}
    />
  );
});

export const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroup.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroup.Item>
>(function ToggleGroupItem({ className, ...props }, ref) {
  return (
    <ToggleGroup.Item
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center h-7 w-8 rounded-sm text-muted hover:text-ink-2 transition-colors",
        // Active = white pill with a soft shadow on the bg-bg track — prototype's `.view-toggle button.is-active`.
        "data-[state=on]:bg-paper data-[state=on]:text-ink data-[state=on]:shadow-sm",
        "focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2",
        className,
      )}
      {...props}
    />
  );
});
